import { useEffect, useState } from 'react';
import {
  DASHBOARD_CATEGORIES,
  DEFAULT_DASHBOARD_VIEW,
  type DashboardCategory,
  type DashboardView,
} from '@ehr/shared';
import { api } from './api.js';

/**
 * The signed-in user's Dashboard View (ehr#60, Users & Profile → Dashboard View): which
 * contact category tabs show, in what order and under what label, and whether the
 * Referrals / Export / Notifications buttons show. Fetched once and shared; saving in the
 * Dashboard View window fires DASHBOARD_VIEW_EVENT so open screens refetch.
 */
export const DASHBOARD_VIEW_EVENT = 'credify:dashboard-view-changed';

let cached: DashboardView | null = null;
let inflight: Promise<DashboardView> | null = null;
const load = (fresh = false): Promise<DashboardView> => {
  if (!fresh && cached) return Promise.resolve(cached);
  if (!fresh && inflight) return inflight;
  inflight = api
    .get<DashboardView>('/me/dashboard-view')
    .then((v) => (cached = v ?? DEFAULT_DASHBOARD_VIEW))
    .catch(() => (cached = cached ?? DEFAULT_DASHBOARD_VIEW))
    .finally(() => (inflight = null));
  return inflight;
};

export function useDashboardView(): DashboardView {
  const [view, setView] = useState<DashboardView>(cached ?? DEFAULT_DASHBOARD_VIEW);
  useEffect(() => {
    let alive = true;
    void load().then((v) => alive && setView(v));
    const onChange = () => void load(true).then((v) => alive && setView(v));
    window.addEventListener(DASHBOARD_VIEW_EVENT, onChange);
    return () => {
      alive = false;
      window.removeEventListener(DASHBOARD_VIEW_EVENT, onChange);
    };
  }, []);
  return view;
}

/** The category keys to show, in order (never empty). */
export function visibleCategories(view: DashboardView): DashboardCategory[] {
  const order = [
    ...view.order,
    ...DASHBOARD_CATEGORIES.filter((c) => !view.order.includes(c)),
  ] as DashboardCategory[];
  const shown = order.filter((c) => view.visible[c] !== false);
  return shown.length ? shown : order;
}

export const buttonShown = (
  view: DashboardView,
  b: 'referral_bucket' | 'export' | 'notifications',
) => view.buttons[b] !== false;
